import { useState } from 'react';
import { I18n } from 'react-redux-i18n';
import SvgDocumentFile from '../../../../assets/images/icons/DocumentFile';
import SvgExternalLink from '../../../../assets/images/icons/ExternalLink';

const C = {
  accent: '#e25d34',
  accentHover: '#c94e27',
  accentSoft: 'rgba(226,93,52,0.07)',
  accentSofter: 'rgba(226,93,52,0.04)',
  textFaint: '#A8B5C2',
  border: '#E3E8ED',
  borderLight: '#EEF1F4',
  bgSubtle: '#F9FAFB',
  white: '#FFFFFF'
};

type DocumentItem = {
  current_batch_id: number;
  new_wine_list_alert_id: number;
  wl_url?: string | null;
  wl_path?: string | null;
  wl_filename?: string | null;
  has_loaded_checks?: number | null;
};

const FileRow = ({ doc, isLast }: { doc: DocumentItem; isLast: boolean }) => {
  const [hovered, setHovered] = useState(false);
  const filename = doc.wl_filename || I18n.t('storePopup.tabs.documents');
  const isPdf = filename.toLowerCase().endsWith('.pdf');

  return (
    <a
      href={doc.wl_url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 12px',
        borderBottom: isLast ? 'none' : `1px solid ${C.borderLight}`,
        background: hovered ? C.accentSofter : 'transparent',
        borderRadius: 6,
        cursor: 'pointer',
        transition: 'background 0.15s',
        margin: '0 -12px',
        textDecoration: 'none'
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 7,
          background: C.accentSoft,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <SvgDocumentFile stroke={C.accent} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: hovered ? C.accentHover : C.accent,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            transition: 'color 0.15s'
          }}
        >
          {filename}
        </div>
        <div
          style={{
            fontSize: 10.5,
            color: C.textFaint,
            marginTop: 5,
            marginBottom: 3,
            fontWeight: 500
          }}
        >
          Wine List{isPdf && ' · PDF'}
        </div>
      </div>

      <SvgExternalLink
        stroke={C.textFaint}
        style={{
          opacity: hovered ? 0.8 : 0.3,
          transition: 'opacity 0.15s',
          flexShrink: 0
        }}
      />
    </a>
  );
};

type WineListTabTabProps = {
  documents?: DocumentItem[] | null;
};

export const WineListTab = ({ documents }: WineListTabTabProps) => {
  if (!documents?.length) {
    return (
      <div
        style={{
          padding: '40px 0',
          textAlign: 'center',
          color: C.textFaint,
          fontSize: 13
        }}
      >
        {I18n.t('storePopup.general.collectedWineLists')}
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 0 10px'
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: '#4A5868'
          }}
        >
          {I18n.t('storePopup.general.collectedWineLists')}
        </div>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: C.white,
            background: C.accent,
            borderRadius: 10,
            padding: '2px 9px',
            lineHeight: '16px'
          }}
        >
          {documents.length}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '4px 12px',
          background: C.bgSubtle
        }}
      >
        {documents.map((doc, i) => (
          <FileRow key={i} doc={doc} isLast={i === documents.length - 1} />
        ))}
      </div>
    </>
  );
};
